import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Paper,
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  InputAdornment,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Switch,
  Stack,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LaunchIcon from "@mui/icons-material/Launch";
import axios from "axios";
import ItineraryViewDialog from "./ItineraryViewDialog";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const CapsuleSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 16,
  padding: 0,
  display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: 2,
    top: 0,
    left: 0,
    "&.Mui-checked": {
      transform: "translateX(26px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.error.main,
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 12,
    height: 12,
    borderRadius: "50%",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  },
  "& .MuiSwitch-track": {
    height: 16,
    borderRadius: 8,
    backgroundColor: theme.palette.grey[400],
    opacity: 1,
  },
}));

const getDateString = (daysFromToday = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  return date.toISOString().split("T")[0];
};

export default function BookingsSidebar({
  store_id,
  bookings,
  selectedBooking,
  onNewBooking,
  setSelectedBooking,
  setSelectedCustomerForPanel,
  setSelectedCustomerTrips,
  setSnackbar,
  allVehicles,
  allDrivers,
  onItineraryUse,
  onCustomerSelect,
  onItineraryBookingsFetcherReady,
}) {
  const [leftTab, setLeftTab] = useState(0);
  const [unAssigned, setUnAssigned] = useState(false);
  const [search, setSearch] = useState("");

  // ── CUSTOMERS STATE ───────────────────────────────
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerList, setCustomerList] = useState([]);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerHasMore, setCustomerHasMore] = useState(true);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [isCustomerLoadingMore, setIsCustomerLoadingMore] = useState(false);
  const customerListRef = useRef(null);
  const customerSearchTimeoutRef = useRef(null);

  // ── ITINERARIES STATE ──────────────────────────────
  const [itinerarySearch, setItinerarySearch] = useState("");
  const [itineraryList, setItineraryList] = useState([]);
  const [isItineraryLoading, setIsItineraryLoading] = useState(false);
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [itineraryDialogOpen, setItineraryDialogOpen] = useState(false);
  const [itinerarySubTab, setItinerarySubTab] = useState("itinerary");
  const [itineraryBookings, setItineraryBookings] = useState([]);
  const [isItineraryBookingsLoading, setIsItineraryBookingsLoading] =
    useState(false);

  // ── CUSTOMER LIST FETCHER ──────────────────────────
  const fetchCustomerList = useCallback(
    async (pageNum = 1, isLoadMore = false) => {
      try {
        if (!isLoadMore) setIsCustomerLoading(true);
        else setIsCustomerLoadingMore(true);
        const res = await axios.get(
          `${API_BASE_URL}/api/travel/customers/store/${store_id}`,
          {
            // params: { page: pageNum, limit: 25, search: customerSearch.trim() },
            params: {
              page: pageNum,
              limit: 25,
              search: customerSearch.trim(),
              search_mode: "wild",
            },
          },
        );
        const data = res.data?.data ?? [];
        const pagination = res.data?.pagination;
        if (!res.data.success) throw res.data;
        if (isLoadMore) setCustomerList((prev) => [...prev, ...data]);
        else setCustomerList(data);
        setCustomerTotal(pagination?.total || 0);
        setCustomerHasMore(pagination?.hasNextPage || false);
        setCustomerPage(pageNum);
      } catch (err) {
        setSnackbar({
          open: true,
          message: err?.statusMessage || "Failed to load customers",
          severity: "error",
        });
      } finally {
        setIsCustomerLoading(false);
        setIsCustomerLoadingMore(false);
      }
    },
    [store_id, customerSearch, setSnackbar],
  );

  // ── ITINERARY LIST FETCHER ─────────────────────────
  const fetchItineraryList = useCallback(async () => {
    try {
      setIsItineraryLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/itineraries/store/${store_id}`,
      );
      if (!res.data.success) throw res.data;
      setItineraryList(res.data?.data ?? []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.statusMessage || "Failed to load itineraries",
        severity: "error",
      });
    } finally {
      setIsItineraryLoading(false);
    }
  }, [store_id, setSnackbar]);

  const fetchItineraryBookings = useCallback(async () => {
    try {
      setIsItineraryBookingsLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/booking/store/${store_id}`,
        { params: { is_itinerary: "1" } }, // ← uses the new query param
      );
      if (!res.data.success) throw res.data;
      setItineraryBookings(res.data?.data ?? []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.statusMessage || "Failed to load itinerary bookings",
        severity: "error",
      });
    } finally {
      setIsItineraryBookingsLoading(false);
    }
  }, [store_id, setSnackbar]);
  useEffect(() => {
    if (onItineraryBookingsFetcherReady) {
      onItineraryBookingsFetcherReady(fetchItineraryBookings);
    }
  }, [onItineraryBookingsFetcherReady, fetchItineraryBookings]);

  useEffect(() => {
    if (leftTab !== 1) return;
    if (customerSearchTimeoutRef.current)
      clearTimeout(customerSearchTimeoutRef.current);
    customerSearchTimeoutRef.current = setTimeout(() => {
      setCustomerPage(1);
      setCustomerList([]);
      fetchCustomerList(1, false);
    }, 500);
    return () => {
      if (customerSearchTimeoutRef.current)
        clearTimeout(customerSearchTimeoutRef.current);
    };
  }, [customerSearch, leftTab, fetchCustomerList]);

  useEffect(() => {
    if (leftTab === 1 && customerList.length === 0) fetchCustomerList(1, false);
  }, [leftTab, customerList.length, fetchCustomerList]);

  useEffect(() => {
    if (leftTab === 2) {
      fetchItineraryList();
      fetchItineraryBookings();
    }
  }, [leftTab, fetchItineraryList, fetchItineraryBookings]);

  const handleCustomerScroll = useCallback(() => {
    if (!customerListRef.current || isCustomerLoadingMore || !customerHasMore)
      return;
    const { scrollTop, scrollHeight, clientHeight } = customerListRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100)
      fetchCustomerList(customerPage + 1, true);
  }, [customerPage, customerHasMore, isCustomerLoadingMore, fetchCustomerList]);

  // ── CUSTOMER CLICK HANDLER ─────────────────────────
  const handleCustomerClick = async (customer) => {
    try {
      setIsCustomerLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/customers/store/${store_id}/id/${customer.customer_id}`,
      );
      if (!res.data.success) throw res.data;
      const fetched = res.data.data;

      // Update right panel (past trips)
      setSelectedCustomerForPanel(fetched);
      setSelectedCustomerTrips(fetched.trips || []);

      // ← NEW: autofill BookingForm with this customer's details
      if (onCustomerSelect) {
        onCustomerSelect(fetched);
      }

      setSnackbar({
        open: true,
        message: `Customer "${fetched.name}" loaded`,
        severity: "success",
      });
    } catch (err) {
      console.error("Fetch customer error:", err);
      setSnackbar({
        open: true,
        message: err?.statusMessage || "Failed to load customer",
        severity: "error",
      });
    } finally {
      setIsCustomerLoading(false);
    }
  };

  // ── BOOKING SELECT HANDLER ─────────────────────────
  const handleBookingSelect = async (booking) => {
    setSelectedBooking(booking);

    if (booking.customer_id) {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/travel/customers/store/${store_id}/id/${booking.customer_id}`,
        );
        if (res.data.success) {
          const fetched = res.data.data;
          setSelectedCustomerForPanel(fetched);
          setSelectedCustomerTrips(fetched.trips || []);
        }
      } catch (err) {
        console.error("Failed to fetch customer trips for booking:", err);
      }
    }
  };

  // ── ITINERARY SELECT HANDLER ───────────────────────
  const handleItineraryClick = (itinerary) => {
    setSelectedItinerary(itinerary);
    setItineraryDialogOpen(true);
  };

  // ── OPEN ITINERARY PDF IN NEW TAB ─────────────────
  const openPdfInNewTab = (e, itineraryDoc) => {
    e.stopPropagation(); // don't trigger booking select
    if (!itineraryDoc?.file) return;
    const fileUrl = itineraryDoc.file;
    // If it's a remote URL (http/https), open directly
    if (fileUrl.startsWith("http")) {
      window.open(fileUrl, "_blank");
      return;
    }
    // If it's a base64 data URI, convert to Blob URL
    try {
      const base64 = fileUrl.includes(",") ? fileUrl.split(",")[1] : fileUrl;
      const bytes = atob(base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      console.error("Failed to open PDF:", err);
    }
  };

  const handleNewBooking = () => {
    setSelectedBooking(null);
    setSelectedCustomerForPanel(null);
    setSelectedCustomerTrips([]);
    if (onNewBooking) onNewBooking(); // ← triggers BookingForm reset
  };

  const filteredBookings = bookings.filter((b) => {
    const searchTerm = (search || "").toLowerCase();
    const matchesSearch =
      (b.customer_name || "").toLowerCase().includes(searchTerm) ||
      (b.customer_mobile || "").toString().includes(searchTerm) ||
      (b.booking_id || "").toLowerCase().includes(searchTerm);
    const matchesUnAssigned = unAssigned ? !b.driver_id || !b.vehicle_id : true;
    return matchesSearch && matchesUnAssigned;
  });

  const filteredItineraries = itineraryList.filter(
    (item) =>
      item.name.toLowerCase().includes(itinerarySearch.toLowerCase()) ||
      item.description.toLowerCase().includes(itinerarySearch.toLowerCase()),
  );

  const bookingTypeFilteredList = itineraryBookings.filter((b) =>
    itinerarySubTab === "bookings"
      ? b.booking_type === "B"
      : itinerarySubTab === "enquiries"
      ? b.booking_type === "E"
      : true,
  );

  return (
    <>
      <Paper
        sx={{
          // On < lg the sidebar is the only visible pane and sits under the
          // sticky page header + pane switcher, so it gets a shorter viewport
          // budget; its inner lists keep scrolling on their own. Below md the
          // app shell also reserves a top header + bottom nav bar.
          height: {
            xs: "calc(100vh - 250px)",
            md: "calc(100vh - 200px)",
            lg: "calc(100vh - 120px)",
          },
          minHeight: 300,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Tabs
          value={leftTab}
          onChange={(_, newVal) => setLeftTab(newVal)}
          variant="fullWidth"
          sx={{
            borderBottom: "1px solid #e0e0e0",
            minHeight: 42,
            "& .MuiTab-root": {
              minHeight: 42,
              fontSize: "0.75rem",
              fontWeight: 600,
              textTransform: "none",
              padding: "6px 8px",
            },
            "& .Mui-selected": { color: "#1976d2" },
            "& .MuiTabs-indicator": { backgroundColor: "#1976d2" },
          }}
        >
          <Tab label="Trips" />
          <Tab label="Customers" />
          <Tab label="Itinerary" />
        </Tabs>

        {/* ── TRIPS TAB ── */}
        {leftTab === 0 && (
          <Box
            sx={{
              p: { xs: 1.25, sm: 2 },
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              fullWidth
              size="small"
              sx={{ mb: 1.5 }}
              onClick={handleNewBooking}
            >
              New Booking
            </Button>
            <TextField
              fullWidth
              size="small"
              placeholder="Search bookings..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Box display="flex" alignItems="center" gap={1} sx={{ my: 1 }}>
              <Typography
                variant="body2"
                color={unAssigned ? "#B2B2B2" : "#7B6ED6"}
                fontWeight={unAssigned ? 0 : 600}
              >
                All
              </Typography>
              <CapsuleSwitch
                checked={unAssigned}
                onChange={(e) => setUnAssigned(e.target.checked)}
              />
              <Typography
                variant="body2"
                color={!unAssigned ? "#B2B2B2" : "#7B6ED6"}
                fontWeight={unAssigned ? 600 : 0}
              >
                Unassigned
              </Typography>
            </Box>
            <List sx={{ flex: 1, overflowY: "auto", mt: 0.5 }} dense>
              {filteredBookings.map((booking) => (
                <React.Fragment key={booking.booking_id}>
                  <ListItem
                    onClick={() => handleBookingSelect(booking)}
                    sx={{
                      cursor: "pointer",
                      bgcolor:
                        selectedBooking?.booking_id === booking.booking_id
                          ? "action.selected"
                          : "transparent",
                      "&:hover": { bgcolor: "action.hover" },
                      py: 1,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ width: 36, height: 36 }}>
                        {(booking.customer_name || "?").charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500}>
                          {booking.customer_name || "Unknown"}
                          {((!booking.driver_id && !booking.temp_driver) ||
                            (!booking.vehicle_id && !booking.temp_vehicle)) && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="error"
                              sx={{ marginLeft: 0.5 }}
                            >
                              (Unassigned)
                            </Typography>
                          )}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" display="block">
                            {booking.customer_mobile || "N/A"}
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={booking.booking_id || "N/A"}
                              size="small"
                              sx={{ mr: 0.5, fontSize: 9, height: 18 }}
                            />
                            <Chip
                              label={
                                booking.booking_type === "E"
                                  ? "Enquiry"
                                  : "Booking"
                              }
                              size="small"
                              color={
                                booking.booking_type === "B"
                                  ? "success"
                                  : "warning"
                              }
                              sx={{ fontSize: 9, height: 18 }}
                            />
                          </Box>
                          {/* ── PDF chip if itinerary attached ── */}
                          {/* {booking.itinerary_doc?.file && (
                            <Tooltip
                              title={`Open PDF: ${
                                booking.itinerary_doc.fileName || "Itinerary"
                              }`}
                              arrow
                            >
                              <Chip
                                icon={
                                  <PictureAsPdfIcon
                                    sx={{ fontSize: "11px !important" }}
                                  />
                                }
                                label={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.3,
                                    }}
                                  >
                                    <span
                                      style={{
                                        maxWidth: 110,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        display: "inline-block",
                                        fontSize: 9,
                                      }}
                                    >
                                      {booking.itinerary_doc.fileName ||
                                        "Itinerary.pdf"}
                                    </span>
                                    <LaunchIcon sx={{ fontSize: 9 }} />
                                  </Box>
                                }
                                size="small"
                                onClick={(e) =>
                                  openPdfInNewTab(e, booking.itinerary_doc)
                                }
                                sx={{
                                  mt: 0.5,
                                  height: 20,
                                  fontSize: 9,
                                  bgcolor: "#fff3e0",
                                  color: "#e65100",
                                  border: "1px solid #ffcc80",
                                  cursor: "pointer",
                                  "& .MuiChip-label": {
                                    display: "flex",
                                    alignItems: "center",
                                    px: 0.5,
                                  },
                                  "&:hover": { bgcolor: "#ffe0b2" },
                                }}
                              />
                            </Tooltip>
                          )} */}
                        </>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {/* ── CUSTOMERS TAB ── */}
        {leftTab === 1 && (
          <Box
            sx={{
              p: { xs: 1.25, sm: 2 },
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search customers..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              {customerTotal} customer{customerTotal !== 1 ? "s" : ""} found
            </Typography>
            <List
              ref={customerListRef}
              onScroll={handleCustomerScroll}
              sx={{
                flex: 1,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 0, height: 0 },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
              dense
            >
              {customerList.map((customer, idx) => (
                <React.Fragment key={customer.customer_id}>
                  <ListItem
                    onClick={() => handleCustomerClick(customer)}
                    sx={{
                      cursor: "pointer",
                      borderRadius: 1,
                      mb: 0.25,
                      bgcolor: "transparent",
                      "&:hover": { bgcolor: "action.hover" },
                      py: 0.75,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          backgroundColor: "#1976d2",
                          width: 34,
                          height: 34,
                          fontSize: "0.85rem",
                        }}
                      >
                        {(customer.name || "?")[0].toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {customer.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {customer.mobile}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {idx < customerList.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              {isCustomerLoadingMore && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              )}
              {!customerHasMore && customerList.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                  sx={{ display: "block", py: 1.5 }}
                >
                  No more customers
                </Typography>
              )}
              {!isCustomerLoading && customerList.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ py: 3 }}
                >
                  No customers found
                </Typography>
              )}
            </List>
            {isCustomerLoading && customerList.length === 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            )}
          </Box>
        )}

        {/* ── ITINERARY TAB ── */}
        {leftTab === 2 && (
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Sub-tabs */}
            <Tabs
              value={itinerarySubTab}
              onChange={(_, val) => {
                setItinerarySubTab(val);
                if (val === "bookings" || val === "enquiries") {
                  fetchItineraryBookings();
                }
              }}
              variant="fullWidth"
              sx={{
                minHeight: 34,
                borderBottom: "1px solid #e5e7eb",
                "& .MuiTab-root": {
                  minHeight: 34,
                  fontSize: { xs: "0.62rem", sm: "0.7rem" },
                  fontWeight: 600,
                  textTransform: "none",
                  py: 0.5,
                  px: { xs: 0.5, sm: 1 },
                  minWidth: 0,
                },
                "& .Mui-selected": { color: "#1976d2" },
                "& .MuiTabs-indicator": { backgroundColor: "#1976d2" },
              }}
            >
              <Tab label="Itineraries" value="itinerary" />
              <Tab
                label={`Bookings (${
                  itineraryBookings.filter((b) => b.booking_type === "B").length
                })`}
                value="bookings"
              />
              <Tab
                label={`Enquiries (${
                  itineraryBookings.filter((b) => b.booking_type === "E").length
                })`}
                value="enquiries"
              />
            </Tabs>

            {/* ── ITINERARY SUB-TAB ── */}
            {itinerarySubTab === "itinerary" && (
              <Box
                sx={{
                  p: { xs: 1.25, sm: 2 },
                  flex: 1,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search itineraries..."
                  value={itinerarySearch}
                  onChange={(e) => setItinerarySearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1.5 }}
                />
                <Box sx={{ flex: 1, overflowY: "auto" }}>
                  <Stack spacing={1}>
                    {filteredItineraries.map((itinerary) => (
                      <Paper
                        key={itinerary.id}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          cursor: "pointer",
                          border: "1px solid #e5e7eb",
                          bgcolor: "#fff",
                          "&:hover": {
                            bgcolor: "#f0f9ff",
                            borderColor: "#1976d2",
                          },
                        }}
                        onClick={() => handleItineraryClick(itinerary)}
                      >
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          spacing={1}
                        >
                          <MapRoundedIcon
                            sx={{ fontSize: 20, color: "#1976d2", mt: 0.3 }}
                          />
                          <Box flex={1}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ mb: 0.5 }}
                            >
                              {itinerary.name}
                            </Typography>
                            <Chip
                              icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}
                              label={`${itinerary.total_days} ${
                                itinerary.total_days === 1 ? "day" : "days"
                              }`}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                bgcolor: "#e3f2fd",
                                color: "#1976d2",
                              }}
                            />
                            {itinerary.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  mt: 0.5,
                                }}
                              >
                                {itinerary.description}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                    {filteredItineraries.length === 0 &&
                      !isItineraryLoading && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          align="center"
                          sx={{ py: 4 }}
                        >
                          No itineraries found
                        </Typography>
                      )}
                  </Stack>
                </Box>
                {isItineraryLoading && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 3 }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                )}
              </Box>
            )}

            {/* ── ITINERARY BOOKINGS SUB-TAB ── */}
            {(itinerarySubTab === "bookings" ||
              itinerarySubTab === "enquiries") && (
              <Box
                sx={{
                  p: { xs: 1.25, sm: 2 },
                  flex: 1,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {isItineraryBookingsLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 3 }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : bookingTypeFilteredList.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ py: 4 }}
                  >
                    No{" "}
                    {itinerarySubTab === "bookings" ? "bookings" : "enquiries"}{" "}
                    found
                  </Typography>
                ) : (
                  <Box sx={{ flex: 1, overflowY: "auto" }}>
                    <Stack spacing={1}>
                      {bookingTypeFilteredList.map((booking) => (
                        <Paper
                          key={booking.booking_id}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            cursor: "pointer",
                            border: "1px solid #e5e7eb",
                            bgcolor: "#fff",
                            "&:hover": {
                              bgcolor: "#f0f9ff",
                              borderColor: "#1976d2",
                            },
                          }}
                          onClick={() => handleBookingSelect(booking)}
                        >
                          <Stack
                            direction="row"
                            alignItems="flex-start"
                            spacing={1}
                          >
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: "0.75rem",
                                bgcolor: "#1976d2",
                                mt: 0.2,
                                flexShrink: 0,
                              }}
                            >
                              {(booking.customer_name || "?")[0].toUpperCase()}
                            </Avatar>
                            <Box flex={1} minWidth={0}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                sx={{ mb: 0.4 }}
                              >
                                {booking.customer_name || "Unknown"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mb: 0.4 }}
                              >
                                {booking.customer_mobile || "N/A"}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <Chip
                                  label={booking.booking_id}
                                  size="small"
                                  sx={{ mr: 0.5, fontSize: 9, height: 18 }}
                                />
                                <Chip
                                  label={
                                    booking.booking_type === "E"
                                      ? "Enquiry"
                                      : "Booking"
                                  }
                                  size="small"
                                  color={
                                    booking.booking_type === "B"
                                      ? "success"
                                      : "warning"
                                  }
                                  sx={{ fontSize: 9, height: 18 }}
                                />
                                {/* {booking.pickup_date && (
                                  <Chip
                                    icon={
                                      <CalendarTodayIcon
                                        sx={{ fontSize: "10px !important" }}
                                      />
                                    }
                                    label={booking.pickup_date}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: "0.65rem",
                                      bgcolor: "#e3f2fd",
                                      color: "#1976d2",
                                    }}
                                  />
                                )} */}
                              </Stack>
                            </Box>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Itinerary View Dialog */}
      <ItineraryViewDialog
        open={itineraryDialogOpen}
        onClose={() => {
          setItineraryDialogOpen(false);
          setSelectedItinerary(null);
        }}
        itinerary={selectedItinerary}
        onUse={onItineraryUse}
        setSnackbar={setSnackbar}
      />
    </>
  );
}
